import { css } from "@emotion/css";
import type { SimpleMenuItem, PrimaryPageAction, PageAction } from "@octopusdeploy/design-system-components";
import { Button, EmptyStateDashboardIllustration, EmptyStateDashboardNoPermissionIllustration, ExternalLink } from "@octopusdeploy/design-system-components";
import { borderRadius, colorScales, fontSize, fontWeight, letterSpacing, lineHeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import type { AnalyticActionDispatcher } from "~/analytics/Analytics";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { projectStatusActions } from "~/areas/projects/components/ProjectStatus/actions";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import { Level1EmptyStateLayout } from "~/components/GettingStarted/Level1EmptyStateLayout";
import { PermissionCheck } from "~/components/PermissionCheck";
import type { DoBusyTask } from "../DataBaseComponent";
import NewProjectWizard from "./NewProjectWizard";
import { ProjectsGettingStartedGuides } from "./ProjectsGettingStartedGuides";
interface OnboardingProject {
    Name: string;
    Slug: string;
    LogoUrl?: string;
}
interface DashboardOnboardingLayoutProps {
    spaceId: string;
    canContinueOnboarding: boolean;
    onboardingProject?: OnboardingProject;
    header?: {
        title: string;
        primaryAction?: PrimaryPageAction;
        actions?: PageAction[];
        overflowActions?: SimpleMenuItem[];
    };
    doBusyTask: DoBusyTask;
    projectStatus: ProjectStatus;
}
function DashboardOnboardingLayout({ spaceId, canContinueOnboarding, onboardingProject, header, doBusyTask, projectStatus }: DashboardOnboardingLayoutProps) {
    const [openDialog, setOpenDialog] = useState(false);
    const dispatchAction = useAnalyticActionDispatch();
    const isOnboardingHalfReady = isOnboardingHalfDone(canContinueOnboarding, onboardingProject);
    const onboardingTitle = isOnboardingHalfReady ? "Project Setup" : "Deploy your first application";
    const onboardingIntro = isOnboardingHalfReady ? (<div className={onboardingIntroContainerStyles}>
            {onboardingProject?.LogoUrl && (<div className={logoContainerStyles}>
                    <img className={logoStyles} src={onboardingProject?.LogoUrl} alt="Project Logo"/>
                </div>)}
            <div className={continueOnboardingIntroStyles}>
                <strong>{onboardingProject.Name}</strong> is almost setup
            </div>
        </div>) : ("We'll guide you along the process of adding your first project through to releasing your first deployment.");
    const learnMoreLink = (<ExternalLink video href="OnboardingGettingStartedVideo">
            Video (3 mins)
        </ExternalLink>);
    const onboardingActions = isOnboardingHalfReady ? [ContinueProjectSetup(setOpenDialog, projectStatus)] : [GettingStarted(dispatchAction, setOpenDialog), learnMoreLink];
    const closeDialog = (project: ProjectResource | undefined) => {
        if (project) {
            dispatchAction("Save First Project", { action: Action.Save, resource: "Project" });
        }
        else {
            dispatchAction("Cancel First Project Creation", { action: Action.Cancel, resource: "Project" });
        }
        setOpenDialog(false);
    };
    return (<PermissionCheck permission={Permission.ProjectCreate} wildcard alternate={<NoActionDashboard />}>
            <Level1EmptyStateLayout title={onboardingTitle} titleStyles={isOnboardingHalfDone(canContinueOnboarding, onboardingProject) ? continueTitleStyles : undefined} intro={onboardingIntro} actionButtonsAndLinks={onboardingActions} image={<EmptyStateDashboardIllustration />} analyticLocation={"Onboarding dashboard"} header={header} bottomSection={<ProjectsGettingStartedGuides />}/>

            <NewProjectWizard open={openDialog} spaceId={spaceId} projectSlug={onboardingProject?.Slug} isOnboarding={isOnboardingHalfReady} doBusyTask={doBusyTask} close={(project) => (isOnboardingHalfReady ? setOpenDialog(false) : closeDialog(project))} projectStatus={projectStatus}/>
        </PermissionCheck>);
}
function ContinueProjectSetup(setOpenDialog: (open: boolean) => void, projectStatus: ProjectStatus) {
    return (<Button importance="primary" label="Continue" onClick={() => {
            setOpenDialog(true);
            projectStatus.dispatch({ type: projectStatusActions.guidedSetupResumed });
        }}/>);
}
function GettingStarted(dispatchAction: AnalyticActionDispatcher, setOpenDialog: (open: boolean) => void) {
    return (<Button importance="primary" label="Get Started" onClick={() => {
            dispatchAction("Add First Project", { action: Action.Add, resource: "Project" });
            setOpenDialog(true);
        }}/>);
}
function isOnboardingHalfDone(canContinueOnboarding: boolean, onboardingProject: OnboardingProject | undefined): onboardingProject is OnboardingProject {
    return canContinueOnboarding && onboardingProject !== undefined;
}
function NoActionDashboard() {
    return (<Level1EmptyStateLayout title={"Permissions"} intro={"No projects have been setup yet. Your current permissions don\u2019t allow you to create projects. If you require more information or believe this is in error please contact your administrator. "} image={<EmptyStateDashboardNoPermissionIllustration />}/>);
}
const onboardingIntroContainerStyles = css({
    display: "flex",
    alignItems: "center",
});
const logoContainerStyles = css({
    marginRight: space[12],
    width: "2.25rem",
    height: "2.25rem",
    borderRadius: borderRadius.medium,
    background: colorScales.white,
});
const logoStyles = css({
    width: "100%",
    height: "100%",
    objectFit: "scale-down",
    borderRadius: borderRadius.small,
});
const continueOnboardingIntroStyles = css({
    fontSize: fontSize.xLarge,
    lineHeight: lineHeight.large,
});
const continueTitleStyles = css({
    all: "unset",
    font: text.body.bold.large,
    fontWeight: fontWeight["700"],
    letterSpacing: letterSpacing.wider,
    color: themeTokens.color.text.secondary,
});
export default DashboardOnboardingLayout;
